import { useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface EnumOption {
  /** Canonical English value persisted to user_data. */
  value: string;
  /** Localized label shown in the dropdown. */
  label: string;
}

interface EnumMultiSelectProps {
  options: EnumOption[];
  /** Selected canonical (English) values. */
  selected: string[];
  onChange: (selected: string[]) => void;
  /**
   * Fires when the popover closes. `committed` distinguishes a Done /
   * outside-click commit (true) from an explicit Cancel (false).
   */
  onClose?: (committed: boolean) => void;
  placeholder: string;
  emptyLabel: string;
  /** Footer button label that commits the current selection. */
  doneLabel: string;
  /** Footer button label that closes without saving. */
  cancelLabel: string;
  /**
   * Whether to auto-open the popover on mount. Sidebar's edit flow opens
   * immediately (the row is already in edit mode); the dashboard's edit
   * mode wants the trigger button to start closed and open on click.
   * Default true to preserve existing behaviour.
   */
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Generic multi-select dropdown for enum_multiselect attributes. Selected values
 * are the canonical English strings (value), not the localized labels — saving
 * stays consistent with the building extractor's allowed_values contract.
 */
export function EnumMultiSelect({
  options,
  selected,
  onChange,
  onClose,
  placeholder,
  emptyLabel,
  doneLabel,
  cancelLabel,
  defaultOpen = true,
  className,
}: EnumMultiSelectProps) {
  const [open, setOpen] = useState(defaultOpen);
  // Radix only fires `onOpenChange` for dismissals IT initiates (outside-click,
  // Escape, trigger) — never when we mutate the controlled `open` prop. So the
  // Done / Cancel buttons must call `onClose` themselves; relying on
  // `onOpenChange` left Done silently never saving and Cancel never exiting edit
  // mode. `closedRef` guards against a double fire if a Radix version also emits
  // `onOpenChange` for our programmatic close. Reset on (re)open.
  const closedRef = useRef(false);
  const fireClose = (committed: boolean) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose?.(committed);
  };

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  // Selected values that aren't in the official options list — typically a
  // free-form string the EHR / extractor produced (e.g. "plekk" against a
  // roof-finish enum). Render them as plain checkboxes at the END of the
  // list so the user can uncheck them individually. They disappear once
  // unchecked, since they're not real options that can be re-selected;
  // Cancel still restores the original selection on the next open.
  const optionValues = new Set(options.map((o) => o.value));
  const extraSelected = selected.filter((v) => !optionValues.has(v));

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          // New open-session — allow the next close to fire onClose.
          closedRef.current = false;
        } else {
          // Radix-initiated close (outside-click / Escape) commits, mirroring
          // the existing "click away saves" behaviour.
          fireClose(true);
        }
      }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("h-7 w-full justify-between px-2 text-xs font-normal", className)}
        >
          <span className="truncate">
            {selected.length === 0
              ? placeholder
              : selected.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ")}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end">
        <Command>
          <CommandList>
            {options.length === 0 && extraSelected.length === 0 ? (
              <CommandEmpty>{emptyLabel}</CommandEmpty>
            ) : (
              <CommandGroup>
                {options.map((opt) => {
                  const isSelected = selected.includes(opt.value);
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => toggle(opt.value)}
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </div>
                      <span>{opt.label}</span>
                    </CommandItem>
                  );
                })}
                {extraSelected.map((v) => (
                  <CommandItem key={`extra-${v}`} value={v} onSelect={() => toggle(v)}>
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        "bg-primary text-primary-foreground"
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                    <span>{v}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        <div className="flex items-center justify-end gap-2 border-t border-border px-2 py-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => {
              fireClose(false);
              setOpen(false);
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 px-3 text-xs"
            onClick={() => {
              fireClose(true);
              setOpen(false);
            }}
          >
            {doneLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
